'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/app/context/AuthContext';
import { useDialog } from '@/app/hooks/useDialog';
import { MODAL_BACKDROP, MODAL_PANEL } from '@/app/components/ui/motion';

interface AuthPromptModalProps {
  open: boolean;
  onClose: () => void;
  /** Called when user successfully authenticates (before onClose) */
  onAuthSuccess?: () => void;
  /** Message shown to explain why login is needed */
  message?: string;
}

export default function AuthPromptModal({ open, onClose, onAuthSuccess, message }: AuthPromptModalProps) {
  const router = useRouter();
  const { signIn, signInWithMagicLink, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<'password' | 'magic-link'>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Close when user becomes authenticated, and fire success callback
  useEffect(() => {
    if (user && open) {
      onAuthSuccess?.();
      onClose();
    }
  }, [user, open, onClose, onAuthSuccess]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setEmail('');
      setPassword('');
      setError('');
      setLoading(false);
      setMode('password');
      setMagicLinkSent(false);
    }
  }, [open]);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { error } = await signIn(email, password);
    if (error) {
      setError(error);
      setLoading(false);
    }
    // onClose will be triggered by the user effect above
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setLoading(true);
    setError('');
    const { error } = await signInWithMagicLink(email);
    if (error) {
      setError(error);
      setLoading(false);
    } else {
      setMagicLinkSent(true);
      setLoading(false);
    }
  };

  const handleCreateAccount = useCallback(() => {
    const currentPath = window.location.pathname + window.location.hash;
    router.push(`/signup?redirect=${encodeURIComponent(currentPath)}`);
    onClose();
  }, [router, onClose]);

  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  const dialogRef = useDialog<HTMLDivElement>({ open, onClose });

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 ${MODAL_BACKDROP}`}
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        className={`w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-xl shadow-2xl overflow-hidden ${MODAL_PANEL}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-0">
          <h2 id="auth-modal-title" className="text-lg font-semibold text-zinc-100">Sign in to continue</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors rounded-lg hover:bg-zinc-800"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Context message */}
        <p className="px-6 pt-2 text-sm text-zinc-400">
          {message || 'Create a free account or sign in to save your work and access it from any device.'}
        </p>

        <div className="p-6">
          {magicLinkSent ? (
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-zinc-300">
                We&apos;ve sent a magic link to <span className="text-zinc-100 font-medium">{email}</span>
              </p>
              <p className="text-xs text-zinc-500">Check your inbox and click the link to sign in. You can close this popup.</p>
              <button
                onClick={() => setMagicLinkSent(false)}
                className="text-sm text-emerald-500 hover:text-emerald-400 transition-colors"
              >
                Try a different method
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div id="auth-modal-error" role="alert" className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {mode === 'password' ? (
                <>
                  <form onSubmit={handlePasswordLogin} className="space-y-3">
                    <div>
                      <label htmlFor="auth-email" className="block text-sm font-medium text-zinc-400 mb-1">Email</label>
                      <input
                        id="auth-email"
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        autoComplete="email"
                        aria-invalid={!!error}
                        aria-describedby={error ? 'auth-modal-error' : undefined}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="auth-password" className="block text-sm font-medium text-zinc-400 mb-1">Password</label>
                      <input
                        id="auth-password"
                        type="password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                        aria-invalid={!!error}
                        aria-describedby={error ? 'auth-modal-error' : undefined}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                        placeholder="Your password"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-600/50 disabled:cursor-not-allowed text-emerald-950 font-medium rounded-lg transition-colors text-sm"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                  </form>

                  {/* Magic link option */}
                  <button
                    onClick={() => { setMode('magic-link'); setError(''); }}
                    className="w-full mt-3 py-2 text-sm text-zinc-400 hover:text-emerald-400 transition-colors"
                  >
                    Sign in with a magic link instead
                  </button>

                  {/* Divider */}
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-zinc-800" />
                    <span className="text-xs text-zinc-500">or</span>
                    <div className="flex-1 h-px bg-zinc-800" />
                  </div>

                  {/* Create account */}
                  <button
                    onClick={handleCreateAccount}
                    className="w-full py-2.5 px-4 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 font-medium rounded-lg transition-colors text-sm"
                  >
                    Create a free account
                  </button>
                </>
              ) : (
                <form onSubmit={handleMagicLink} className="space-y-3">
                  <div>
                    <label htmlFor="auth-magic-email" className="block text-sm font-medium text-zinc-400 mb-1">Email</label>
                    <input
                      id="auth-magic-email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      aria-invalid={!!error}
                      aria-describedby={error ? 'auth-modal-error' : undefined}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm"
                      placeholder="you@example.com"
                    />
                  </div>
                  <p className="text-xs text-zinc-500">
                    We&apos;ll email you a link to sign in — no password needed.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-600/50 disabled:cursor-not-allowed text-emerald-950 font-medium rounded-lg transition-colors text-sm"
                  >
                    {loading ? 'Sending...' : 'Send Magic Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('password'); setError(''); }}
                    className="w-full py-2 text-sm text-zinc-400 hover:text-emerald-400 transition-colors"
                  >
                    Back to password sign in
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
